import { zodResolver } from "@hookform/resolvers/zod"
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import React, { useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { PAYMENT_METHOD_DEFAULT_VALUES } from "../constants"
import PaymentMethodForm from "../forms/PaymentMethodForm"
import { usePaymentMethodDialog } from "../hooks/usePaymentMethodDialog"
import { type PaymentMethodFormValues, PaymentMethodSchema } from "../schemas/PaymentMethodSchema"

import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { type CompanyPaymentType, PaymentAccountType, PaymentMethod } from "@/graphql/codegen/graphql"

interface PaymentMethodDialogProps {
  companyId: string
  onClose: () => void
  onSuccess: () => void
  paymentTypes: CompanyPaymentType[]
}

const PaymentMethodDialog: React.FC<PaymentMethodDialogProps> = ({ companyId, onClose, onSuccess, paymentTypes }) => {
  const methods = useForm<PaymentMethodFormValues>({
    resolver: zodResolver(PaymentMethodSchema),
    mode: "onChange",
    defaultValues: {
      ...PAYMENT_METHOD_DEFAULT_VALUES,
      companyId,
    },
  })

  const { setValue } = methods

  const navigate = useNavigate({ from: "/app/companies/$companyId/edit/accounting" })
  const searchParams = useSearch({ from: "/app/companies/$companyId/edit/_layout/accounting" })

  const handleClose = () => {
    navigate({ search: {} })
    setTimeout(() => onClose(), 100)
  }

  const handleSuccess = () => {
    navigate({ search: {} })
    setTimeout(() => onSuccess(), 100)
  }

  const paymentTypeToCreateFrom = useMemo(() => {
    if (!searchParams.companyPaymentTypeId) {
      return null
    }

    return paymentTypes.find((paymentType) => paymentType.companyPaymentTypeId === searchParams.companyPaymentTypeId)
  }, [paymentTypes, searchParams.companyPaymentTypeId])

  useEffect(() => {
    if (paymentTypeToCreateFrom) {
      setValue("bankId", paymentTypeToCreateFrom.bankId || "")
      setValue("method", paymentTypeToCreateFrom.paymentMethods?.[0]?.method || PaymentMethod.Wire)
      setValue("locationId", paymentTypeToCreateFrom.billingLocations?.[0]?.locationId || "")
      setValue("accountType", paymentTypeToCreateFrom.accountType || PaymentAccountType.Receivable)
      setValue("bankLocationId", paymentTypeToCreateFrom.bankLocationId || "")
    }
  }, [paymentTypeToCreateFrom, setValue])

  const { isCancelConfirmationOpen, setIsCancelConfirmationOpen, canSubmit, handleSubmit, handleCancel } =
    usePaymentMethodDialog({
      methods,
      companyId,
      onSuccess: handleSuccess,
      onClose: handleClose,
    })

  return (
    <>
      <Dialog
        open
        onClose={handleCancel}
        maxWidth='sm'
        fullWidth
        sx={{ display: isCancelConfirmationOpen ? "none" : "block" }}
      >
        <DialogHeader title=' Billing and Payment Information' onClose={handleCancel} />
        <DialogContent classes={{ root: "px-10" }}>
          <FormProvider {...methods}>
            <PaymentMethodForm companyId={companyId} paymentTypeToCreateFrom={paymentTypeToCreateFrom} />
          </FormProvider>
        </DialogContent>

        <DialogActions className='justify-between px-10 pb-10 pt-4'>
          <Button onClick={handleCancel} variant='outlined' color='primary'>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant='contained' color='primary' disabled={!canSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {isCancelConfirmationOpen && (
        <CancelDialog onConfirm={handleClose} onClose={() => setIsCancelConfirmationOpen(false)} />
      )}
    </>
  )
}

export default PaymentMethodDialog
